import {
  $getRoot,
  EditorState,
  LexicalEditor,
  createEditor,
  $createParagraphNode,
  $createTextNode,
  ParagraphNode
} from "lexical";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { ListNode, ListItemNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { CodeNode, CodeHighlightNode } from "@lexical/code";
import { ImageNode } from "@/lib/lexical/nodes/ImageNode";
import { InlineImageNode } from "@/lib/lexical/nodes/InlineImageNode";

/**
 * Decode HTML entities in a string (e.g., &lt;p&gt; -> <p>)
 */
export function decodeHtmlEntities(html: string): string {
  if (!html) return "";
  // Uses the browser's DOM parsing capabilities to decode entities.
  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  return textarea.value;
}

/**
 * Repeatedly decode HTML entities until the string no longer changes
 */
export function fullyDecodeHtmlEntities(html: string): string {
  let prev = html;
  let curr = decodeHtmlEntities(prev);
  while (curr !== prev) {
    prev = curr;
    curr = decodeHtmlEntities(prev);
  }
  return curr;
}

/**
 * Sanitizes a string to be a valid HTML ID.
 *
 * HTML IDs can only contain:
 * - Letters (a-z, A-Z)
 * - Digits (0-9)
 * - Hyphens (-)
 * - Underscores (_)
 *
 * All other characters (spaces, parentheses, special chars) are replaced with hyphens.
 *
 * @param id - The string to sanitize
 * @returns A valid HTML ID string
 *
 * @example
 * sanitizeHtmlId("Playing Ability Test (PAT)") // "Playing-Ability-Test--PAT-"
 * sanitizeHtmlId("Associate Program Application") // "Associate-Program-Application"
 */
export function sanitizeHtmlId(id: string): string {
  return id.replace(/[^a-zA-Z0-9-_]/g, "-");
}

/**
 * Converts a Lexical JSON string to HTML
 * @param lexicalJson The Lexical JSON string representation of editor state
 * @returns HTML string
 */
export function convertLexicalToHtml(lexicalJson: string): string {
  try {
    // Create a temporary editor
    const editor = createEditor({
      namespace: "htmlConverter",
      theme: {
        list: {
          checklist: "editor-checklist",
          listitemChecked: "editor-listitem-checked",
          listitemUnchecked: "editor-listitem-unchecked"
        }
      },
      onError: (error: Error) => {
        console.error("Error converting Lexical to HTML:", error);
      },
      nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        LinkNode,
        AutoLinkNode,
        ParagraphNode,
        CodeNode,
        CodeHighlightNode,
        ImageNode,
        InlineImageNode
      ]
    });

    try {
      // Set the editor state from the provided JSON
      const editorState = editor.parseEditorState(lexicalJson);

      // Generate HTML from the editor state
      let html = "";
      editorState.read(() => {
        html = $generateHtmlFromNodes(editor);
      });

      return applyExportedHtmlEnhancements(html);
    } finally {
      // Clean up
      editor._keyToDOMMap?.clear();
    }
  } catch (error) {
    console.error("Failed to convert Lexical JSON to HTML:", error);
    return ""; // Return empty string in case of error
  }
}

/**
 * Converts HTML to Lexical JSON
 * @param html The HTML string to convert
 * @returns Lexical JSON string
 */
export function convertHtmlToLexical(html: string): string {
  try {
    // Create a temporary DOM element to use for conversion
    const parser = new DOMParser();
    // Sanitize HTML before parsing to prevent XSS
    const dom = parser.parseFromString(html, "text/html");

    // Create a temporary Lexical editor instance
    const editor = createEditor({
      namespace: "htmlConverter",
      theme: {},
      onError: (error: Error) => {
        console.error("Error converting HTML to Lexical:", error);
      },
      nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        LinkNode,
        AutoLinkNode,
        ParagraphNode,
        CodeNode,
        CodeHighlightNode,
        ImageNode,
        InlineImageNode
      ]
    });

    // Generate Lexical nodes from HTML DOM
    let result = "";
    editor.update(() => {
      // Pre-process DOM to handle styles that custom nodes might not pick up directly
      // or for other general transformations.
      // The data-lexical-text-align logic is removed as custom nodes handle style attributes.
      const processNode = (node: HTMLElement) => {
        // Check for style attribute for other potential transformations if needed in the future
        const styleAttr = node.getAttribute("style");
        if (styleAttr) {
          // Example: Extract white-space if present and needed by custom nodes
          const whiteSpaceMatch = styleAttr.match(/white-space:\s*([^;]+)/i);
          if (whiteSpaceMatch && whiteSpaceMatch[1]) {
            const whiteSpaceValue = whiteSpaceMatch[1].trim();
            node.setAttribute("data-white-space", whiteSpaceValue); // If custom nodes use this
          }
        }

        // Special handling for underline tags (remains useful)
        if (node.tagName.toLowerCase() === "u") {
          node.style.textDecoration = "underline";
        }

        // Process children recursively
        Array.from(node.children).forEach((child) => {
          if (child instanceof HTMLElement) {
            processNode(child);
          }
        });
      };

      if (dom.body) {
        processNode(dom.body);
      }

      try {
        // Generate Lexical nodes from the processed DOM
        // Custom nodes' importDOM methods should handle text-align from style attributes.
        const nodes = $generateNodesFromDOM(editor, dom);

        $getRoot().clear();

        if (nodes.length === 0) {
          const paragraph = $createParagraphNode();
          $getRoot().append(paragraph);
        } else {
          // Logic for setting format based on data-lexical-text-align is removed.
          // Custom nodes should handle their formatting during importDOM.
          nodes.forEach((node) => {
            $getRoot().append(node);
          });
        }
      } catch (domError) {
        console.error("Error generating nodes from DOM:", domError);
        const paragraph = $createParagraphNode();
        $getRoot().append(paragraph);
      }
    });

    // Get the JSON representation of the Lexical state
    result = JSON.stringify(editor.getEditorState().toJSON());
    console.debug("Generated Lexical JSON from HTML");

    // Clean up
    editor._keyToDOMMap?.clear();

    return result;
  } catch (error) {
    console.error("Failed to convert HTML to Lexical JSON:", error, "HTML:", html.substring(0, 100));
    return ""; // Return empty string in case of error
  }
}

/**
 * Initialize Lexical editor with HTML content
 * @param editor The LexicalEditor instance
 * @param htmlContent HTML string content
 */
export function initEditorWithHtml(editor: LexicalEditor, htmlContent?: string): void {
  if (!htmlContent || htmlContent.trim() === "") {
    createDefaultParagraph(editor);
    return;
  }

  try {
    let processedHtml = htmlContent;
    if (!/<\/?[a-z][\s\S]*>/i.test(processedHtml)) {
      processedHtml = decodeHtmlEntities(processedHtml);
    }

    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(processedHtml);

    if (!hasHtmlTags) {
      editor.update(() => {
        try {
          const root = $getRoot();
          root.clear();

          // Split by paragraphs and create paragraph nodes
          const paragraphs = processedHtml.split(/\n\n+/);
          paragraphs.forEach((text: string) => {
            const paragraphNode = $createParagraphNode();
            const textNode = $createTextNode(text);
            paragraphNode.append(textNode);
            root.append(paragraphNode);
          });
        } catch (error) {
          createDefaultParagraph(editor);
        }
      }, editorUpdateOptions(editor));
      return;
    }

    const parser = new DOMParser();
    const dom = parser.parseFromString(processedHtml, "text/html");

    // Process the DOM for any necessary transformations before Lexical parsing.
    // Text-align handling via data-text-align is removed; custom nodes will use style attributes.
    const processNode = (node: HTMLElement) => {
      // If node is a paragraph, check for markdown list pattern and preserve it
      if (node.tagName.toLowerCase() === "p") {
        const content = node.textContent || "";
        if (/^[-*]\s/.test(content) || /^[0-9]+\.\s/.test(content)) {
          node.setAttribute("data-preserve-markdown", "true");
        }
      }

      // Process children recursively
      Array.from(node.children).forEach((child) => {
        if (child instanceof HTMLElement) {
          processNode(child);
        }
      });
    };

    if (dom.body) {
      processNode(dom.body);
    }

    editor.update(() => {
      try {
        const root = $getRoot();
        root.clear();
        const nodes = $generateNodesFromDOM(editor, dom);

        if (nodes.length === 0) {
          const paragraph = $createParagraphNode();
          const textNode = $createTextNode(processedHtml);
          paragraph.append(textNode);
          root.append(paragraph);
        } else {
          try {
            nodes.forEach((node, _index) => {
              if (node.getType() !== "text") {
                root.append(node);
              } else {
                const paragraph = $createParagraphNode();
                paragraph.append(node);
                root.append(paragraph);
              }
            });
          } catch (appendError) {
            const paragraph = $createParagraphNode();
            const textNode = $createTextNode(processedHtml);
            paragraph.append(textNode);
            root.append(paragraph);
          }
        }
      } catch (error) {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        const textNode = $createTextNode(processedHtml);
        paragraph.append(textNode);
        root.append(paragraph);
      }
    }, editorUpdateOptions(editor));
  } catch (error) {
    createDefaultParagraph(editor);
  }
}

function editorUpdateOptions(editor: LexicalEditor): { discrete: true } | undefined {
  return editor.getRootElement() == null ? { discrete: true } : undefined;
}

/**
 * Create a default paragraph in the editor
 * @param editor The LexicalEditor instance
 */
function createDefaultParagraph(editor: LexicalEditor): void {
  editor.update(() => {
    const root = $getRoot();
    root.clear();
    const paragraph = $createParagraphNode();
    root.append(paragraph);
  }, editorUpdateOptions(editor));
}

function isChecklistListElement(element: Element): boolean {
  const listType = element.getAttribute("__lexicallisttype") ?? element.getAttribute("__lexicalListType");
  if (listType === "check") return true;
  if (element.classList.contains("editor-checklist") || element.classList.contains("contains-task-list")) {
    return true;
  }
  return Array.from(element.children).some((child) => child.hasAttribute("aria-checked"));
}

/**
 * Post-process Lexical HTML so bullet/numbered lists keep fallback styles
 * without rewriting checklists into disc lists.
 */
export function applyExportedHtmlEnhancements(html: string): string {
  const withUnderline = html.replace(/<span style="text-decoration: underline;">(.*?)<\/span>/g, "<u>$1</u>");
  const parser = new DOMParser();
  const doc = parser.parseFromString(withUnderline, "text/html");

  for (const ul of Array.from(doc.querySelectorAll("ul"))) {
    if (isChecklistListElement(ul)) {
      ul.classList.add("editor-checklist");
      if (!(ul.getAttribute("__lexicallisttype") ?? ul.getAttribute("__lexicalListType"))) {
        ul.setAttribute("__lexicalListType", "check");
      }
      continue;
    }
    if (!ul.style.listStyleType) {
      ul.style.listStyleType = "disc";
    }
  }

  for (const ol of Array.from(doc.querySelectorAll("ol"))) {
    if (!ol.style.listStyleType) {
      ol.style.listStyleType = "decimal";
    }
  }

  for (const li of Array.from(doc.querySelectorAll("li"))) {
    if (li.getAttribute("role") === "checkbox") continue;
    if (!li.style.marginLeft) {
      li.style.marginLeft = "20px";
    }
  }

  return doc.body.innerHTML;
}

/**
 * Handle editor changes by converting to HTML
 * @param editorState The current Lexical editor state
 * @param onChange Callback function that receives the HTML string
 */
export function handleEditorChangeToHtml(editorState: EditorState, onChange: (content: string) => void): void {
  const jsonString = JSON.stringify(editorState.toJSON());
  const htmlString = convertLexicalToHtml(jsonString);
  onChange?.(htmlString);
}

/**
 * Check if HTML content from Rich Text Editor is effectively empty
 * This accounts for empty HTML tags, whitespace, and non-breaking spaces
 * @param htmlContent The HTML string to check
 * @returns true if the content is effectively empty, false otherwise
 */
export function isRichTextContentEmpty(htmlContent: string | null | undefined): boolean {
  if (!htmlContent || typeof htmlContent !== "string") {
    return true;
  }

  // Remove HTML tags and decode entities to ensure field is empty
  const textContent = htmlContent
    .replace(/<[^>]*>/g, "") // Remove all HTML tags
    .replace(/&nbsp;/g, " ") // Replace non-breaking spaces with regular spaces
    .replace(/&amp;/g, "&") // Decode common HTML entities
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim(); // Remove leading and trailing whitespace

  return textContent.length === 0;
}
